import './App.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayModule } from './PlayModule';
import { useAppDispatch, useAppSelector, bootFile, bootRemote } from './Actions';
import { LibraryEntry, deleteEntry, loadLocalEntries, loadServerEntries, putEntry } from './Library';
import { identifyDisc } from './DiscInfo';
import {
	SLOT_COUNT,
	SaveState,
	deleteSlot,
	exportState,
	formatStateSize,
	importState,
	listStates,
	loadFromSlot,
	saveToSlot,
} from './SaveStates';
import About from './About';
import { GamepadInfo, onGamepadChange, startGamepadPolling, stopGamepadPolling } from './Gamepad';
import {
	Key,
	Lang,
	formatLastPlayedI18n,
	formatSizeI18n,
	useLang,
} from './i18n';
import { COMPAT_KEY, CompatEntry, loadCompat, lookupCompat } from './Compat';
import { Platform, formatHeap, readHeap, readPlatform } from './SystemStatus';


/* --------------------------------------------------------------------- demo */

/**
 * Jeu de demonstration, servi par le site lui-meme.
 *
 * Doom porte sur PS2 (homebrew, 7 Mo) : de quoi essayer l'emulateur en trois
 * secondes sans fournir d'image disque. C'est la premiere chose que voit un
 * visiteur dont la bibliotheque est vide.
 *
 * DECLARE ICI, PAS DANS library.json : nginx repond 404 sur ce fichier en
 * production (voir nginx.conf), volontairement — le site ne distribue aucun
 * catalogue. La demo doit donc exister meme sans catalogue.
 *
 * L'entree n'est PAS versee dans `entries`, qui represente la bibliotheque du
 * visiteur : `entries.length === 0` commande tout l'ecran d'accueil, et
 * l'injecter le ferait disparaitre a jamais. Elle est rendue a part, dans les
 * deux branches. Son `id` est une constante de module : `open()` compare
 * `booted === entry.id` pour reprendre au lieu de redemarrer, ce qu'un objet
 * recree a chaque rendu casserait.
 */
const DEMO: LibraryEntry = {
	id: 'demo:ps2doom',
	title: 'Doom',
	code: '',
	region: 'NTSC',
	size: 7028736,
	origin: 'server',
	url: '/doom-demo.iso',
	lastPlayed: null,
};

/**
 * Delai maximal d'attente de la premiere image, en millisecondes.
 *
 * Le voile de demarrage se retire des que l'emulateur a rendu une image, ce
 * qui evite les deux ou trois secondes d'ecran noir qui suivent le montage du
 * disque. Mais si la VM ne rend jamais rien (disque illisible, faute WASM),
 * une attente non bornee laisserait un voile perpetuel — pire que l'ecran noir
 * qu'on corrige. Au-dela de ce delai on retire le voile quand meme.
 */
const FIRST_FRAME_TIMEOUT = 15000;

/**
 * Attend que l'emulateur affiche autre chose que du noir, sans jamais depasser
 * FIRST_FRAME_TIMEOUT.
 *
 * NE PAS SE FIER A getFrames(). Le compteur grimpe des que la machine
 * virtuelle tourne, pas quand une image utile parait : mesure faite, il vaut
 * deja 17 apres 200 ms alors que l'ecran reste noir 2,7 s de plus. C'est la
 * console qui demarre pendant ce temps -- le disque, lui, est deja monte. Un
 * voile qui se leve sur ce critere retombe donc immediatement et ne masque
 * rien du tout.
 *
 * On echantillonne l'image a la place, en la reduisant a 64x48 : le voile se
 * leve quand du contenu apparait vraiment.
 */
const BLACK_LEVEL = 6; // luminance moyenne (0-255) en deca de laquelle l'image est noire

async function waitFirstFrame(): Promise<void> {
	const deadline = Date.now() + FIRST_FRAME_TIMEOUT;
	const canvas = document.getElementById('outputCanvas') as HTMLCanvasElement | null;
	const probe = document.createElement('canvas');
	probe.width = 64;
	probe.height = 48;
	const ctx = probe.getContext('2d', { willReadFrequently: true });
	if (!canvas || !ctx) {
		// Sans canevas lisible, mieux vaut rendre la main que bloquer l'ecran.
		return;
	}
	while (Date.now() < deadline) {
		await new Promise((resolve) => setTimeout(resolve, 120));
		try {
			ctx.drawImage(canvas, 0, 0, probe.width, probe.height);
			const data = ctx.getImageData(0, 0, probe.width, probe.height).data;
			let sum = 0;
			for (let i = 0; i < data.length; i += 4) sum += data[i] + data[i + 1] + data[i + 2];
			if (sum / (data.length / 4) / 3 > BLACK_LEVEL) return;
		} catch {
			/* canevas pas encore dessinable : on retente */
		}
	}
	// Delai expire : on rend la main sans signaler d'erreur. Le disque a bien
	// ete monte -- parler d'un defaut de format serait faux et trompeur.
}

/* ------------------------------------------------------------------ entrees */

/**
 * Boutons remappables.
 *
 * L'ordre suit STRICTEMENT PS2::CControllerInfo::BUTTON (ControllerInfo.h:10) :
 * les axes d'abord, puis la croix directionnelle, Select/Start, les boutons de
 * face, enfin les gachettes. Se tromper ici fait ecrire les liaisons sur les
 * mauvais boutons — et ecrase silencieusement celles de la manette.
 */
const BUTTONS: Array<{ index: number; label: string; labelEn: string }> = [
	{ index: 0, label: 'Analog. G ←→', labelEn: 'Left stick ←→' },
	{ index: 1, label: 'Analog. G ↑↓', labelEn: 'Left stick ↑↓' },
	{ index: 2, label: 'Analog. D ←→', labelEn: 'Right stick ←→' },
	{ index: 3, label: 'Analog. D ↑↓', labelEn: 'Right stick ↑↓' },
	{ index: 4, label: 'Haut', labelEn: 'Up' },
	{ index: 5, label: 'Bas', labelEn: 'Down' },
	{ index: 6, label: 'Gauche', labelEn: 'Left' },
	{ index: 7, label: 'Droite', labelEn: 'Right' },
	{ index: 8, label: 'Select', labelEn: 'Select' },
	{ index: 9, label: 'Start', labelEn: 'Start' },
	{ index: 10, label: 'Carré', labelEn: 'Square' },
	{ index: 11, label: 'Triangle', labelEn: 'Triangle' },
	{ index: 12, label: 'Rond', labelEn: 'Circle' },
	{ index: 13, label: 'Croix', labelEn: 'Cross' },
	{ index: 14, label: 'L1', labelEn: 'L1' },
	{ index: 15, label: 'L2', labelEn: 'L2' },
	{ index: 16, label: 'L3', labelEn: 'L3' },
	{ index: 17, label: 'R1', labelEn: 'R1' },
	{ index: 18, label: 'R2', labelEn: 'R2' },
	{ index: 19, label: 'R3', labelEn: 'R3' },
];

/** Les quatre premiers sont des axes : ils demandent deux touches. */
const AXIS_COUNT = 4;

const DEFAULT_KEYS: Record<number, string | [string, string]> = {
	0: ['KeyF', 'KeyH'],
	1: ['KeyT', 'KeyG'],
	2: ['KeyJ', 'KeyL'],
	3: ['KeyI', 'KeyK'],
	4: 'ArrowUp',
	5: 'ArrowDown',
	6: 'ArrowLeft',
	7: 'ArrowRight',
	8: 'Backspace',
	9: 'Enter',
	10: 'KeyA',
	11: 'KeyS',
	12: 'KeyX',
	13: 'KeyZ',
	14: 'Key1',
	15: 'Key2',
	16: 'Key3',
	17: 'Key8',
	18: 'Key9',
	19: 'Key0',
};

// v2 : la version precedente enregistrait les liaisons sur les mauvais index
// (ordre de BUTTON mal transcrit). Changer la cle invalide ces mappages.
const KEYMAP_STORAGE = 'ps2-keymap-v2';

/** Drapeau de premiere visite : le tutoriel ne s'impose qu'une fois. */
const TUTORIAL_STORAGE = 'ps2-tutorial-vu';

/**
 * Hauteur, en pixels depuis le haut de la vue de jeu, sous laquelle le curseur
 * fait revenir le HUD. Un peu plus que la barre elle-meme (46 px) : sinon elle
 * se derobe des qu'on vise un de ses boutons.
 */
const HUD_REVEAL = 64;

/** Idem depuis le bas, pour le panneau de statistiques. */
const STATS_REVEAL = 96;

/** Volume, conserve d'une visite a l'autre. */
const VOLUME_STORAGE = 'ps2-volume';

/**
 * Statistiques relevees a chaque seconde.
 *
 * `null` signifie « non mesurable sur cette plateforme » et fait disparaitre la
 * ligne, plutot que d'afficher un zero qu'on prendrait pour une mesure. C'est
 * le cas du tas JavaScript hors Chromium, et des compteurs du coeur si le
 * Play.js charge est anterieur a ces liaisons.
 */
interface Stats {
	drawCalls: number | null;
	ee: number | null;
	iop: number | null;
	wasmBytes: number | null;
	heapBytes: number | null;
}

const EMPTY_STATS: Stats = {
	drawCalls: null,
	ee: null,
	iop: null,
	wasmBytes: null,
	heapBytes: null,
};

/** Detection sommaire, uniquement pour afficher un avertissement. */
const IS_FIREFOX = typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox');

/** Nom lisible d'un KeyboardEvent.code. */
function keyLabel(code: string, lang: Lang): string {
	if (code.startsWith('Key')) return code.slice(3);
	if (code.startsWith('Digit')) return code.slice(5);
	if (code.startsWith('Arrow')) return { Up: '↑', Down: '↓', Left: '←', Right: '→' }[code.slice(5)] ?? code;
	if (code === 'Enter') return lang === 'fr' ? 'Entrée' : 'Enter';
	if (code === 'Backspace') return lang === 'fr' ? 'Retour' : 'Backsp.';
	if (code === 'Space') return lang === 'fr' ? 'Espace' : 'Space';
	if (code.startsWith('Numpad')) return `${lang === 'fr' ? 'Num' : 'Num'} ${code.slice(6)}`;
	return code;
}

/* --------------------------------------------------------------- selecteur */

function LangSwitch() {
	const { lang, setLang } = useLang();
	return (
		<span className="lang">
			<button className={lang === 'fr' ? 'lang-on' : undefined} onClick={() => setLang('fr')}>
				FR
			</button>
			<button className={lang === 'en' ? 'lang-on' : undefined} onClick={() => setLang('en')}>
				EN
			</button>
		</span>
	);
}

/* ------------------------------------------------------------- rail d'etat */

/**
 * Rail d'etat systeme. Chaque cellule est une mesure : rien n'est affiche
 * dont la valeur ne soit lue du navigateur. La cellule memoire disparait
 * quand l'API n'existe pas (Firefox, Safari) plutot que d'afficher un tiret
 * qu'on prendrait pour zero.
 */
function StatusBar({ platform, pad }: { platform: Platform; pad: GamepadInfo }) {
	const { t, lang } = useLang();
	const [heap, setHeap] = useState<number | null>(readHeap);

	useEffect(() => {
		if (readHeap() === null) return;
		const timer = setInterval(() => setHeap(readHeap()), 2000);
		return () => clearInterval(timer);
	}, []);

	return (
		<div className="statusbar">
			<span className="status-cell">
				<span className="status-key">{t('status.wasm')}</span>
				<span className={platform.wasm ? undefined : 'status-off'}>
					{t(platform.wasm ? 'status.wasm.ready' : 'status.wasm.absent')}
				</span>
			</span>
			<span className="status-cell">
				<span className="status-key">{t('status.sab')}</span>
				<span className={platform.sab ? 'status-on' : 'status-off'}>
					{t(platform.sab ? 'status.sab.isolated' : 'status.sab.missing')}
				</span>
			</span>
			<span className="status-cell">
				<span className="status-key">{t('status.bios')}</span>
				<span>{t('status.bios.hle')}</span>
			</span>
			{platform.cores > 0 && (
				<span className="status-cell">
					<span className="status-key">{t('status.cores')}</span>
					<span>{platform.cores}</span>
				</span>
			)}
			{heap !== null && (
				<span className="status-cell">
					<span className="status-key">{t('status.mem')}</span>
					<span>{formatHeap(heap, lang)}</span>
				</span>
			)}
			<span className="status-cell">
				<span className="status-key">{t('status.pad')}</span>
				<span className={pad.connected ? 'status-on' : 'status-idle'}>
					{pad.connected ? pad.id.slice(0, 18) : t('status.pad.none')}
				</span>
			</span>
		</div>
	);
}

/* ---------------------------------------------------------------- tutoriel */

type TipName = 'add' | 'nav' | 'entry' | 'ctrl' | null;

/** Infobulle du tutoriel. Purement decorative : jamais focalisable. */
function Tip({ show, text, style }: { show: boolean; text: string; style: React.CSSProperties }) {
	return (
		<span className={show ? 'tip tip-on' : 'tip'} style={style} aria-hidden={!show}>
			{text}
		</span>
	);
}

/* ------------------------------------------------------------------- app */

type View = 'library' | 'about';

function App() {
	const state = useAppSelector((s) => s.play);
	const dispatch = useAppDispatch();
	const { t, lang } = useLang();

	const [view, setView] = useState<View>('library');
	const [entries, setEntries] = useState<LibraryEntry[]>([]);
	const [active, setActive] = useState<LibraryEntry | null>(null);
	/** Jeu deja demarre dans la VM : sert a distinguer reprise et demarrage. */
	const [booted, setBooted] = useState<string | null>(null);
	const [booting, setBooting] = useState(false);
	/** Message affiche quand une image disque n'a pas pu etre lue. */
	const [bootError, setBootError] = useState<string | null>(null);
	const [dragging, setDragging] = useState(false);
	const [panel, setPanel] = useState<'none' | 'settings' | 'states'>('none');
	const [scale, setScale] = useState(1);
	const [fps, setFps] = useState(0);
	const [slots, setSlots] = useState<(SaveState | null)[]>([]);
	const [busy, setBusy] = useState('');
	/** Le HUD dort tant que le curseur ne monte pas en haut de l'ecran. */
	const [hud, setHud] = useState(false);
	/** Panneau de statistiques : meme principe, en bas de l'ecran. */
	const [statsPanel, setStatsPanel] = useState(false);
	const [stats, setStats] = useState<Stats>(EMPTY_STATS);
	/**
	 * Volume, de 0 a 1. Applique dans le processeur sonore emule : le portage
	 * web n'a pas de mixeur systeme a sa disposition.
	 */
	const [volume, setVolume] = useState<number>(() => {
		try {
			const stored = parseFloat(localStorage.getItem(VOLUME_STORAGE) ?? '');
			return Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : 1;
		} catch {
			return 1;
		}
	});
	const [compatReady, setCompatReady] = useState(false);
	const [tip, setTip] = useState<TipName>(null);
	const [tutorial, setTutorial] = useState(false);
	const platform = useMemo(readPlatform, []);

	const [keymap, setKeymap] = useState<Record<number, string | [string, string]>>(() => {
		try {
			const stored = localStorage.getItem(KEYMAP_STORAGE);
			return stored ? { ...DEFAULT_KEYS, ...JSON.parse(stored) } : { ...DEFAULT_KEYS };
		} catch {
			return { ...DEFAULT_KEYS };
		}
	});
	const [capturing, setCapturing] = useState<{ index: number; side: 0 | 1 } | null>(null);
	const [pad, setPad] = useState<GamepadInfo>({ connected: false, id: '' });
	/**
	 * Source d'entree active. Un bouton PS2 ne porte qu'une liaison a la fois,
	 * clavier et manette ne peuvent donc pas cohabiter : on bascule d'un bloc.
	 * 'auto' suit la presence d'une manette ; 'keyboard' et 'gamepad' forcent.
	 */
	const [inputMode, setInputMode] = useState<'auto' | 'keyboard' | 'gamepad'>(() => {
		try {
			return (localStorage.getItem('ps2-input-mode') as any) ?? 'auto';
		} catch {
			return 'auto';
		}
	});

	const stageRef = useRef<HTMLDivElement | null>(null);
	const playerRef = useRef<HTMLDivElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const importInputRef = useRef<HTMLInputElement | null>(null);
	const importSlotRef = useRef<number>(0);

	/* -- bibliotheque -- */
	useEffect(() => {
		let cancelled = false;
		Promise.all([loadServerEntries(), loadLocalEntries()]).then(([server, local]) => {
			if (!cancelled) setEntries([...server, ...local]);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	/*
	 * Registre de compatibilite : charge en arriere-plan, sans bloquer. La
	 * colonne se remplit quand il arrive ; son absence n'est pas une erreur.
	 */
	useEffect(() => {
		let cancelled = false;
		loadCompat().then(() => {
			if (!cancelled) setCompatReady(true);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	/* -- tutoriel de premiere visite -- */
	useEffect(() => {
		try {
			if (!localStorage.getItem(TUTORIAL_STORAGE)) setTutorial(true);
		} catch {
			/* stockage indisponible : pas de tutoriel plutot qu'a chaque visite */
		}
	}, []);

	const dismissTutorial = useCallback(() => {
		setTutorial(false);
		setTip(null);
		try {
			localStorage.setItem(TUTORIAL_STORAGE, '1');
		} catch {
			/* stockage indisponible */
		}
	}, []);

	/*
	 * Le canevas est declare dans index.html, hors de React : le code C++ le
	 * retrouve par "#outputCanvas" au demarrage et y attache ses ecouteurs
	 * clavier. On ne le recree jamais — on le deplace.
	 */
	useEffect(() => {
		const canvas = document.getElementById('outputCanvas') as HTMLCanvasElement | null;
		if (!canvas) return;
		if (active && stageRef.current) {
			canvas.classList.remove('canvas-parked');
			stageRef.current.appendChild(canvas);
			canvas.focus();
		} else {
			canvas.classList.add('canvas-parked');
			document.body.appendChild(canvas);
		}
	}, [active]);

	/* -- taille de presentation : suit le canevas -- */
	useEffect(() => {
		if (!active || !PlayModule?.setPresentationSize) return;
		const canvas = document.getElementById('outputCanvas') as HTMLCanvasElement | null;
		if (!canvas) return;
		const apply = () => {
			const rect = canvas.getBoundingClientRect();
			if (rect.width < 2 || rect.height < 2) return;
			canvas.width = Math.round(rect.width);
			canvas.height = Math.round(rect.height);
			PlayModule.setPresentationSize(canvas.width, canvas.height);
		};
		apply();
		const observer = new ResizeObserver(apply);
		observer.observe(canvas);
		return () => observer.disconnect();
	}, [active]);

	/*
	 * Manette : on scrute en permanence, y compris dans la bibliotheque, pour
	 * pouvoir signaler la connexion avant meme d'entrer en jeu.
	 */
	useEffect(() => {
		startGamepadPolling();
		const unsubscribe = onGamepadChange(setPad);
		return () => {
			unsubscribe();
			stopGamepadPolling();
		};
	}, []);

	/**
	 * Applique la source d'entree au coeur.
	 *
	 * En mode 'auto', la manette prend la main des qu'elle est branchee et la
	 * rend des qu'elle disparait — c'est le comportement attendu d'une console.
	 */
	const effectiveInput = inputMode === 'auto' ? (pad.connected ? 'gamepad' : 'keyboard') : inputMode;

	useEffect(() => {
		if (!PlayModule?.setInputSource) return;
		PlayModule.setInputSource(effectiveInput === 'gamepad' ? 1 : 0);
	}, [effectiveInput, state.value]);

	/*
	 * Volume : applique a la VM et conserve.
	 *
	 * `state.value` fait partie des dependances a dessein — il change quand un
	 * jeu demarre, et le reset reconstruit les coeurs SPU. Le coeur reapplique
	 * deja la valeur de son cote (CPS2VM::ResetVM), ceci couvre le cas ou
	 * l'utilisateur a change le volume avant meme d'avoir lance un jeu.
	 */
	useEffect(() => {
		PlayModule?.setVolume?.(volume);
		try {
			localStorage.setItem(VOLUME_STORAGE, String(volume));
		} catch {
			/* stockage indisponible */
		}
	}, [volume, state.value]);

	/*
	 * Relevé des statistiques.
	 *
	 * UN SEUL intervalle, qui lit tout puis vide UNE fois. Les compteurs de
	 * CStatsManager sont remis a zero par clearStats() : deux appelants qui
	 * alternent lecture et vidage se voleraient leurs echantillons et
	 * afficheraient des zeros une fois sur deux — panne intermittente, donc
	 * penible a diagnostiquer.
	 *
	 * Il tourne meme panneau ferme : le compteur d'images vit dans le HUD du
	 * haut, et les valeurs doivent deja etre justes a l'ouverture du panneau.
	 */
	useEffect(() => {
		if (!active || booting || !PlayModule) return;
		const timer = setInterval(() => {
			try {
				const frames = PlayModule.getFrames();
				// Fonctions ajoutees au pont C++ : absentes d'un Play.js anterieur,
				// auquel cas la ligne correspondante disparait du panneau.
				const draws = PlayModule.getDrawCalls?.() ?? null;
				const ee = PlayModule.getCpuUsage?.(0) ?? -1;
				const iop = PlayModule.getCpuUsage?.(1) ?? -1;
				PlayModule.clearStats();
				setFps(frames);
				setStats({
					drawCalls: draws,
					ee: ee < 0 ? null : ee,
					iop: iop < 0 ? null : iop,
					wasmBytes: PlayModule.HEAP8?.byteLength ?? null,
					heapBytes: readHeap(),
				});
			} catch {
				/* VM pas prete */
			}
		}, 1000);
		return () => clearInterval(timer);
	}, [active, booting]);

	/* -- capture d'une touche pour le remappage -- */
	useEffect(() => {
		if (!capturing) return;
		const onKey = (event: KeyboardEvent) => {
			event.preventDefault();
			event.stopPropagation();
			if (event.code === 'Escape') {
				setCapturing(null);
				return;
			}
			setKeymap((prev) => {
				const next = { ...prev };
				if (capturing.index < AXIS_COUNT) {
					const pair = [...((prev[capturing.index] as [string, string]) ?? ['', ''])] as [string, string];
					pair[capturing.side] = event.code;
					next[capturing.index] = pair;
					PlayModule?.setAxisBinding?.(capturing.index, pair[0], pair[1]);
				} else {
					next[capturing.index] = event.code;
					PlayModule?.setButtonBinding?.(capturing.index, event.code);
				}
				try {
					localStorage.setItem(KEYMAP_STORAGE, JSON.stringify(next));
				} catch {
					/* stockage indisponible */
				}
				return next;
			});
			setCapturing(null);
		};
		window.addEventListener('keydown', onKey, true);
		return () => window.removeEventListener('keydown', onKey, true);
	}, [capturing]);

	/*
	 * Reapplique le mappage clavier personnalise, et LUI SEUL.
	 *
	 * Chaque bouton n'a qu'une liaison a la fois (m_bindings[pad][button] dans
	 * InputBindingManager). Reappliquer aveuglement tout le clavier ecraserait
	 * donc les liaisons manette posees par initVm — c'est ce qui rendait la
	 * manette inerte. On ne touche qu'aux touches que l'utilisateur a lui-meme
	 * changees.
	 */
	useEffect(() => {
		if (!PlayModule?.setButtonBinding) return;
		// En mode manette, les liaisons viennent d'etre posees par
		// setInputSource : les reecrire avec le clavier les detruirait.
		if (effectiveInput !== 'keyboard') return;
		let custom: Record<string, string | [string, string]> = {};
		try {
			const stored = localStorage.getItem(KEYMAP_STORAGE);
			if (!stored) return;
			custom = JSON.parse(stored);
		} catch {
			return;
		}
		for (const [key, value] of Object.entries(custom)) {
			const index = Number(key);
			if (index < AXIS_COUNT && Array.isArray(value)) {
				PlayModule.setAxisBinding?.(index, value[0], value[1]);
			} else if (typeof value === 'string') {
				PlayModule.setButtonBinding(index, value);
			}
		}
		// Les changements ulterieurs sont appliques a la volee dans le
		// gestionnaire de capture.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.value, effectiveInput]);

	/* -- ajout d'une ISO -- */
	const addFile = useCallback(async (file: File) => {
		const fallback = file.name.replace(/\.(iso|cso|chd|isz|bin|elf)$/i, '');
		const info = await identifyDisc(file, fallback);
		const entry: LibraryEntry = {
			id: `local:${info.code || file.name}:${file.size}`,
			title: info.title,
			code: info.code,
			region: info.region,
			size: file.size,
			origin: 'local',
			lastPlayed: null,
			blob: file,
			fileName: file.name,
		};
		await putEntry(entry);
		setEntries((prev) => [...prev.filter((e) => e.id !== entry.id), entry]);
	}, []);

	const onDrop = useCallback(
		async (event: React.DragEvent) => {
			event.preventDefault();
			setDragging(false);
			for (const file of Array.from(event.dataTransfer.files ?? [])) await addFile(file);
		},
		[addFile],
	);

	/* -- lancement, ou REPRISE si le jeu tourne deja -- */
	const open = useCallback(
		async (entry: LibraryEntry) => {
			setActive(entry);
			setPanel('none');
			// Le HUD s'annonce a l'entree puis se retire : sans cela, rien
			// n'indiquerait qu'il existe une barre a faire revenir.
			setHud(true);

			// Le jeu est deja charge dans la VM : on la relance, sans repartir
			// du debut. C'est tout l'interet de la mise en pause au retour.
			if (booted === entry.id) {
				PlayModule?.resume?.();
				listStates(entry.id).then(setSlots);
				return;
			}

			setBooting(true);
			setBootError(null);
			try {
				if (entry.origin === 'server' && entry.url) {
					// Lecture a la demande par plages HTTP : l'image n'est jamais
					// chargee en entier. Un ISO de 1,4 Go passe par un fetch complet
					// saturait l'onglet avant meme le demarrage.
					const result = await dispatch(bootRemote(entry.url));
					if (bootRemote.rejected.match(result)) {
						setBootError(result.error.message ?? t('err.disc.title'));
						setActive(null);
						return;
					}
				} else if (entry.blob) {
					const file = new File([entry.blob], entry.fileName || 'disc.iso');
					const result = await dispatch(bootFile(file));
					if (bootFile.rejected.match(result)) {
						setBootError(result.error.message ?? t('err.disc.title'));
						setActive(null);
						return;
					}
				} else {
					setActive(null);
					return;
				}
				setBooted(entry.id);
				// Le disque est monte, mais l'emulateur n'a encore rien affiche :
				// suivent deux a trois secondes d'ecran noir. On garde le voile
				// jusqu'a la premiere image plutot que d'inventer une progression.
				await waitFirstFrame();
				const played = { ...entry, lastPlayed: Date.now() };
				if (entry.origin === 'local') await putEntry(played);
				setEntries((prev) => prev.map((e) => (e.id === entry.id ? played : e)));
				listStates(entry.id).then(setSlots);
			} finally {
				setBooting(false);
			}
		},
		[booted, dispatch, t],
	);

	/* -- retour bibliotheque : on met en pause, on ne coupe pas -- */
	const backToLibrary = useCallback(() => {
		PlayModule?.pause?.();
		setActive(null);
		setPanel('none');
		setFps(0);
		setHud(false);
	}, []);

	const remove = useCallback(
		async (entry: LibraryEntry, event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			if (entry.origin !== 'local') return;
			if (booted === entry.id) setBooted(null);
			await deleteEntry(entry.id);
			setEntries((prev) => prev.filter((e) => e.id !== entry.id));
		},
		[booted],
	);

	const goFullscreen = useCallback(() => {
		if (document.fullscreenElement) document.exitFullscreen();
		else playerRef.current?.requestFullscreen?.();
	}, []);

	/* -- sauvegardes -- */
	const doSave = useCallback(
		async (slot: number) => {
			if (!active) return;
			setBusy(`${t('states.saving')} ${slot + 1}…`);
			const saved = await saveToSlot(active.id, slot);
			setSlots((prev) => prev.map((s, i) => (i === slot ? saved : s)));
			setBusy('');
		},
		[active, t],
	);

	const doLoad = useCallback(
		async (slot: number) => {
			if (!active) return;
			setBusy(`${t('states.loading')} ${slot + 1}…`);
			await loadFromSlot(active.id, slot);
			setBusy('');
		},
		[active, t],
	);

	const doDelete = useCallback(
		async (slot: number) => {
			if (!active) return;
			await deleteSlot(active.id, slot);
			setSlots((prev) => prev.map((s, i) => (i === slot ? null : s)));
		},
		[active],
	);

	/* ------------------------------------------------- ecran bloquant : SAB */

	/*
	 * Sans memoire partagee, rien ne peut demarrer. Le dire franchement, avec
	 * les trois conditions et celle qui manque, evite la page figee sur
	 * « initialisation… » qui ne renseigne personne.
	 */
	if (!platform.sab) {
		return (
			<div className="app">
				<div className="topbar">
					<span className="brand">
						PS2<span className="brand-dot">.</span>
					</span>
					<span className="topbar-spacer" />
					<LangSwitch />
				</div>
				<div className="fatal">
					<div className="fatal-inner">
						<div className="fatal-eyebrow">{t('err.sab.eyebrow')}</div>
						<h1 className="display-sm">{t('err.sab.title')}</h1>
						<p className="fatal-body">{t('err.sab.body')}</p>
						<div className="fatal-list">
							<div className={platform.isolated ? 'check-ok' : 'check-fail'}>{t('err.sab.coop')}</div>
							<div className={platform.isolated ? 'check-ok' : 'check-fail'}>{t('err.sab.coep')}</div>
							<div className={platform.secure ? 'check-ok' : 'check-fail'}>{t('err.sab.https')}</div>
						</div>
					</div>
				</div>
				<StatusBar platform={platform} pad={pad} />
			</div>
		);
	}

	/* ---------------------------------------------- ecran d'initialisation */

	if (PlayModule === null) {
		return (
			<div className="app">
				<div className="topbar">
					<span className="brand">
						PS2<span className="brand-dot">.</span>
					</span>
					<span className="topbar-spacer" />
					<LangSwitch />
				</div>
				<div className="boot">
					<h1 className="display">{t('init.title')}</h1>
					<div className="boot-progress">
						<div className="boot-progress-head">
							<span>{t('init.module')}</span>
						</div>
						<div className="boot-bar">
							<span />
						</div>
					</div>
					{/* Chaque ligne est un fait verifie, pas une etape decorative. */}
					<div className="checklist">
						<div className={platform.secure ? 'check-ok' : 'check-fail'}>{t('init.secure')}</div>
						<div className={platform.isolated ? 'check-ok' : 'check-fail'}>{t('init.coop')}</div>
						<div className={platform.sab ? 'check-ok' : 'check-fail'}>{t('init.sab')}</div>
						<div className="check-run">
							{t('init.compile')}
							<span className="caret">▌</span>
						</div>
						<div className="check-wait">{t('init.bios')}</div>
						<div className="check-wait">{t('init.threads')}</div>
					</div>
				</div>
				<StatusBar platform={platform} pad={pad} />
			</div>
		);
	}

	/* ------------------------------------------------------------- vue jeu */

	if (active) {
		return (
			<div
				className="player"
				ref={playerRef}
				/*
				 * Le HUD suit la hauteur du curseur, mesuree sur .player.
				 *
				 * On a d'abord essaye une bande de declenchement en position
				 * absolue : elle interceptait clics et mouvements sur les 56 px
				 * superieurs du canevas en permanence, y compris HUD affiche.
				 * Une mesure sur mousemove ne recouvre rien, et reste juste en
				 * plein ecran ou le referentiel change.
				 */
				onMouseMove={(event) => {
					// Les deux zones dans le MEME gestionnaire : un second
					// ecouteur ferait le meme travail deux fois par mouvement.
					// React absorbe un setState de valeur identique, donc les
					// appels repetes ne coutent pas de rendu.
					const rect = event.currentTarget.getBoundingClientRect();
					const y = event.clientY - rect.top;
					setHud(y < HUD_REVEAL);
					setStatsPanel(y > rect.height - STATS_REVEAL);
				}}
				onMouseLeave={() => {
					// Un tiroir ouvert doit rester atteignable : masquer le HUD
					// retirerait le seul bouton capable de le refermer.
					if (panel === 'none') setHud(false);
					setStatsPanel(false);
				}}
			>
				<div className="player-stage" ref={stageRef} />

				<div className={hud ? 'hud' : 'hud hud-off'}>
					<button className="hud-item" onClick={backToLibrary} tabIndex={-1}>
						{t('game.back')}
					</button>
					<div className="hud-title">
						{active.title}
						<span className="hud-code">{active.code || '—'}</span>
					</div>
					<div className="hud-spacer" />
					{busy && <div className="hud-stat pad-live">{busy}</div>}
					{pad.connected && (
						<div className="hud-stat pad-live" title={pad.id}>
							<span className="pad-dot" />
							{t('game.pad')}
						</div>
					)}
					<div className="hud-fps">
						<b>{fps}</b>
						<span>{t('unit.fps')}</span>
					</div>
					<button
						className={panel === 'states' ? 'hud-item hud-right hud-on' : 'hud-item hud-right'}
						onClick={() => setPanel((p) => (p === 'states' ? 'none' : 'states'))}
						tabIndex={-1}
					>
						{t('game.states')}
					</button>
					<button
						className={panel === 'settings' ? 'hud-item hud-right hud-on' : 'hud-item hud-right'}
						onClick={() => setPanel((p) => (p === 'settings' ? 'none' : 'settings'))}
						tabIndex={-1}
					>
						{t('game.settings')}
					</button>
					<button className="hud-item hud-right" onClick={goFullscreen} tabIndex={-1}>
						{t('game.fullscreen')}
					</button>
				</div>

				{/*
				 * Panneau de statistiques, revele au survol du bas.
				 *
				 * Rien n'y est focalisable : le clavier appartient au canevas,
				 * ou le code C++ ecoute. Toutes les valeurs sont mesurees ; une
				 * ligne dont la source n'existe pas sur la plateforme disparait
				 * au lieu d'afficher un zero trompeur.
				 */}
				<div className={statsPanel ? 'statspanel' : 'statspanel statspanel-off'} aria-hidden={!statsPanel}>
					<div className="stat">
						<span className="stat-key">{t('stats.fps')}</span>
						<span className="stat-val stat-hi">{fps}</span>
					</div>
					{stats.drawCalls !== null && (
						<div className="stat">
							<span className="stat-key">{t('stats.draws')}</span>
							<span className="stat-val">
								{stats.drawCalls}
								<em>{t('stats.draws.unit')}</em>
							</span>
						</div>
					)}
					{stats.ee !== null && (
						<div className="stat">
							<span className="stat-key">{t('stats.ee')}</span>
							<span className="stat-val">{stats.ee.toFixed(0)} %</span>
						</div>
					)}
					{stats.iop !== null && (
						<div className="stat">
							<span className="stat-key">{t('stats.iop')}</span>
							<span className="stat-val">{stats.iop.toFixed(0)} %</span>
						</div>
					)}
					{stats.wasmBytes !== null && (
						<div className="stat">
							<span className="stat-key">{t('stats.wasm')}</span>
							<span className="stat-val">{formatHeap(stats.wasmBytes, lang)}</span>
						</div>
					)}
					{stats.heapBytes !== null && (
						<div className="stat">
							<span className="stat-key">{t('stats.heap')}</span>
							<span className="stat-val">{formatHeap(stats.heapBytes, lang)}</span>
						</div>
					)}
					<div className="stat">
						<span className="stat-key">{t('stats.res')}</span>
						<span className="stat-val">{scale}×</span>
					</div>
					{platform.cores > 0 && (
						<div className="stat">
							<span className="stat-key">{t('stats.cores')}</span>
							<span className="stat-val">{platform.cores}</span>
						</div>
					)}
				</div>

				{/* Au-dessus du HUD : pendant la lecture du disque, aucune de ses
				    commandes n'a de sens. */}
				{booting && (
					<div className="boot-overlay">
						<div className="boot-progress">
							<div className="boot-progress-head">
								<span>{t('game.booting')}</span>
							</div>
							<div className="boot-bar">
								<span />
							</div>
						</div>
					</div>
				)}

				{panel === 'states' && (
					<div className="drawer">
						<div className="drawer-head">
							<span className="drawer-title">{t('states.title')}</span>
							<span className="drawer-count">
								{SLOT_COUNT} {t('states.slots')}
							</span>
						</div>
						{slots.map((slot, index) => (
							<div className="slot" key={index}>
								<div className={slot?.thumbnail ? 'slot-thumb slot-thumb-full' : 'slot-thumb'}>
									{slot?.thumbnail ? <img src={slot.thumbnail} alt="" /> : t('states.empty')}
								</div>
								<div className="slot-info">
									<div className="slot-name">
										{t('states.slot')} {index + 1}
									</div>
									<div className={slot ? 'slot-meta' : 'slot-meta slot-meta-empty'}>
										{slot ? (
											<>
												{formatLastPlayedI18n(slot.createdAt, lang, t)} · {formatStateSize(slot.size)}
											</>
										) : (
											'—'
										)}
									</div>
									<div className="slot-actions">
										<button className="mini" onClick={() => doSave(index)}>
											{slot ? t('states.overwrite') : t('states.save')}
										</button>
										<button
											className={slot ? 'mini mini-on' : 'mini'}
											disabled={!slot}
											onClick={() => doLoad(index)}
										>
											{t('states.load')}
										</button>
										{slot ? (
											<>
												<button
													className="mini"
													onClick={() => exportState(slot, active.title)}
													title={t('states.export')}
												>
													↓
												</button>
												<button
													className="mini mini-danger"
													onClick={() => doDelete(index)}
													title={t('states.delete')}
												>
													×
												</button>
											</>
										) : (
											<button
												className="mini"
												title={t('states.import')}
												onClick={() => {
													importSlotRef.current = index;
													importInputRef.current?.click();
												}}
											>
												↑
											</button>
										)}
									</div>
								</div>
							</div>
						))}
						<div className="drawer-foot">
							{t('states.note')}
							<br />
							{t('states.note2')}
						</div>
						<input
							ref={importInputRef}
							type="file"
							/* Les etats de la version de bureau sont des .zip (Play Data
							   Files/states/*.stN.zip) ; le format est identique. */
							accept=".sta,.zip"
							style={{ display: 'none' }}
							onChange={async (e) => {
								const file = e.target.files?.[0];
								if (file && active) {
									const imported = await importState(active.id, importSlotRef.current, file);
									setSlots((prev) => prev.map((s, i) => (i === importSlotRef.current ? imported : s)));
								}
								e.target.value = '';
							}}
						/>
					</div>
				)}

				{panel === 'settings' && (
					<div className="drawer">
						<div className="drawer-head">
							<span className="drawer-title">{t('set.title')}</span>
						</div>

						{/*
						 * Volume. Applique dans le processeur sonore emule : le
						 * portage web n'a pas de mixeur systeme, et un GainNode
						 * cote navigateur n'est pas atteignable (le contexte
						 * OpenAL vit dans le module WASM).
						 */}
						<div className="drawer-section">
							<span className="drawer-label">{t('set.volume')}</span>
							<div className="volume">
								<button
									className="volume-btn"
									onClick={() => setVolume((v) => (v > 0 ? 0 : 1))}
									title={volume > 0 ? t('set.volume.mute') : t('set.volume.unmute')}
								>
									{volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
								</button>
								<input
									className="volume-range"
									type="range"
									min={0}
									max={100}
									step={1}
									value={Math.round(volume * 100)}
									onChange={(e) => setVolume(Number(e.target.value) / 100)}
									aria-label={t('set.volume')}
								/>
								<span className="volume-val">{Math.round(volume * 100)} %</span>
							</div>
							<span className="drawer-note">{t('set.volume.note')}</span>
						</div>

						<div className="drawer-section">
							<span className="drawer-label">{t('set.resolution')}</span>
							<div className="seg">
								{[1, 2, 4, 8, 16].map((value) => (
									<button
										key={value}
										className={value === scale ? 'seg-item active' : 'seg-item'}
										disabled={!PlayModule.setResolutionFactor}
										onClick={() => {
											setScale(value);
											PlayModule.setResolutionFactor?.(value);
										}}
									>
										{value}×
									</button>
								))}
							</div>
							<span className="drawer-note">{t('set.resolution.note')}</span>
						</div>

						<div className="drawer-section">
							<span className="drawer-label">{t('set.input')}</span>
							<div className="seg">
								{(
									[
										['auto', t('set.input.auto')],
										['keyboard', t('set.input.keyboard')],
										['gamepad', t('set.input.gamepad')],
									] as const
								).map(([value, label]) => (
									<button
										key={value}
										className={value === inputMode ? 'seg-item active' : 'seg-item'}
										disabled={value === 'gamepad' && !pad.connected}
										onClick={() => {
											setInputMode(value);
											try {
												localStorage.setItem('ps2-input-mode', value);
											} catch {
												/* stockage indisponible */
											}
										}}
									>
										{label}
									</button>
								))}
							</div>
							<span className="drawer-note">
								{pad.connected ? (
									<>
										<span className="pad-live">⬤</span> {pad.id.slice(0, 40)}
										<br />
									</>
								) : (
									<>
										{t('set.input.none')}
										<br />
									</>
								)}
								{t('set.input.active')} :{' '}
								<strong>{t(effectiveInput === 'gamepad' ? 'set.input.gamepad' : 'set.input.keyboard')}</strong>
								. {t('set.input.one')}
							</span>
						</div>

						<div
							className={effectiveInput === 'gamepad' ? 'drawer-section disabled-block' : 'drawer-section'}
						>
							<span className="drawer-label">
								{effectiveInput === 'gamepad' ? t('set.keyboard.off') : t('set.keyboard')}{' '}
								{capturing && <em className="capturing-hint">{t('set.keyboard.capture')}</em>}
							</span>
							<div className="keymap">
								{BUTTONS.map((button) => {
									const value = keymap[button.index];
									const isAxis = button.index < AXIS_COUNT;
									return (
										<div className="keymap-row" key={button.index}>
											<span>{lang === 'fr' ? button.label : button.labelEn}</span>
											<span className="keymap-keys">
												{isAxis ? (
													([0, 1] as const).map((side) => (
														<button
															key={side}
															className={
																capturing?.index === button.index && capturing?.side === side
																	? 'keycap capturing'
																	: 'keycap'
															}
															onClick={() => setCapturing({ index: button.index, side })}
														>
															{keyLabel((value as [string, string])?.[side] ?? '—', lang)}
														</button>
													))
												) : (
													<button
														className={
															capturing?.index === button.index ? 'keycap capturing' : 'keycap'
														}
														onClick={() => setCapturing({ index: button.index, side: 0 })}
													>
														{keyLabel((value as string) ?? '—', lang)}
													</button>
												)}
											</span>
										</div>
									);
								})}
							</div>
							<button
								className="mini"
								style={{ alignSelf: 'flex-start' }}
								onClick={() => {
									setKeymap({ ...DEFAULT_KEYS });
									try {
										localStorage.removeItem(KEYMAP_STORAGE);
									} catch {
										/* ignore */
									}
									for (const [key, value] of Object.entries(DEFAULT_KEYS)) {
										const index = Number(key);
										if (Array.isArray(value)) PlayModule.setAxisBinding?.(index, value[0], value[1]);
										else PlayModule.setButtonBinding?.(index, value);
									}
								}}
							>
								{t('set.reset')}
							</button>
						</div>
					</div>
				)}
			</div>
		);
	}

	/* --------------------------------------------------------- bibliotheque */

	const showTip = (name: TipName) => tutorial && tip === name;

	return (
		<div className="app">
			<div className="topbar">
				<span className="brand">
					PS2<span className="brand-dot">.</span>
				</span>
				<span className="topbar-spacer" />
				<button
					className={view === 'library' ? 'tab tab-on' : 'tab'}
					onClick={() => setView('library')}
				>
					{t('nav.library')}
				</button>
				{/*
				 * Page autonome servie par nginx, hors du bundle React : d'ou une
				 * ancre et non un onglet. Le routage cote client ne la connait pas.
				 */}
				<span
					className="tut-anchor"
					onMouseEnter={() => setTip('nav')}
					onMouseLeave={() => setTip(null)}
				>
					<a className="tab" href="/compatibilite">
						{t('nav.compat')}
					</a>
					{tutorial && (
						<>
							<span className="tut-badge" style={{ top: -10, right: -14 }}>
								2
							</span>
							<Tip
								show={showTip('nav')}
								text={t('tut.nav')}
								style={{ top: 'calc(100% + 14px)', right: 0 }}
							/>
						</>
					)}
				</span>
				{/* Journal technique : page statique elle aussi, d'ou l'ancre. */}
				<a className="tab" href="/technique">
					{t('nav.tech')}
				</a>
				<button className={view === 'about' ? 'tab tab-on' : 'tab'} onClick={() => setView('about')}>
					{t('nav.about')}
				</button>
				<LangSwitch />
				{booted && <span className="topbar-meta">{t('status.session')}</span>}
			</div>

			{view === 'about' ? (
				<About />
			) : (
				<div
					className="library"
					onDragOver={(e) => {
						e.preventDefault();
						setDragging(true);
					}}
					onDragLeave={() => setDragging(false)}
					onDrop={onDrop}
				>
					{/* -- bibliotheque vide : l'invitation occupe tout l'ecran -- */}
					{entries.length === 0 ? (
						<div className="empty">
							<div className="empty-head">
								<span className="eyebrow">{t('lib.empty.eyebrow')}</span>
								<h1 className="display-lg">
									{t('lib.empty.head1')}
									<br />
									{t('lib.empty.head2')}
								</h1>
							</div>

							<div className={dragging ? 'dropzone dragging' : 'dropzone'}>
								<span className="dropzone-text">
									{t('lib.drop')}
									<br />
									<span className="dropzone-note">{t('lib.drop.note')}</span>
								</span>
								<span
									className="tut-anchor"
									onMouseEnter={() => setTip('add')}
									onMouseLeave={() => setTip(null)}
								>
									{/* Bouton bordé et non plein : la charte n'admet qu'un
									    seul bouton plein par ecran, et sur celui-ci c'est
									    la demo qui porte l'action principale — le visiteur
									    qui arrive ici n'a justement pas de fichier. */}
									<button className="btn" onClick={() => fileInputRef.current?.click()}>
										{t('lib.add')}
									</button>
									{tutorial && (
										<>
											<span className="tut-badge" style={{ top: -8, right: -8 }}>
												1
											</span>
											<Tip
												show={showTip('add')}
												text={t('tut.add')}
												style={{ top: 'calc(100% + 14px)', left: 0 }}
											/>
										</>
									)}
								</span>
							</div>

							{/* Rien a fournir pour essayer : la demo est servie par le
							    site. C'est la seule action possible d'emblee pour qui
							    n'a pas d'image disque, donc elle prend le pas sur le
							    depot -- sans le masquer. */}
							<div className="demo-card">
								<div className="demo-card-body">
									<span className="eyebrow">{t('demo.eyebrow')}</span>
									<h2 className="demo-card-title">{t('demo.title')}</h2>
									<span className="demo-card-sub">{t('demo.sub')}</span>
									<span className="demo-card-note">{t('demo.note')}</span>
								</div>
								<div className="demo-card-side">
									<button className="btn-solid" onClick={() => open(DEMO)}>
										{booted === DEMO.id ? t('demo.resume') : t('demo.play')}
									</button>
									{/* Ancre indispensable : la page technique traite d'abord
									    des bugs de l'emulateur, le portage Doom n'est qu'une
									    section en fin de course. Sans #doom, le visiteur
									    atterrit sur un tout autre sujet. */}
									<a className="demo-card-link" href="/technique#doom">
										{t('demo.tech')}
									</a>
								</div>
							</div>

							<span className="empty-foot">
								{t('lib.title')} · 0 {t('lib.game')} — {t('lib.persist')}
							</span>
						</div>
					) : (
						<>
							<div className="library-head">
								<h1 className="display">{t('lib.title')}</h1>
								<span className="library-count">
									{entries.length} {entries.length > 1 ? t('lib.games') : t('lib.game')} ·{' '}
									{t('lib.local')}
								</span>
								<span className="topbar-spacer" />
								{/* Le repere 1 suit le bouton d'ajout, ici comme sur
								    l'ecran vide : c'est le meme geste. */}
								<span
									className="tut-anchor"
									onMouseEnter={() => setTip('add')}
									onMouseLeave={() => setTip(null)}
								>
									<button
										className="btn-solid btn-solid-sm"
										onClick={() => fileInputRef.current?.click()}
									>
										{t('lib.add.short')}
									</button>
									{tutorial && (
										<>
											<span className="tut-badge" style={{ top: -8, right: -8 }}>
												1
											</span>
											<Tip
												show={showTip('add')}
												text={t('tut.add')}
												style={{ top: 'calc(100% + 14px)', right: 0 }}
											/>
										</>
									)}
								</span>
							</div>

							{/* Deux defauts mesures subsistent sous Firefox : ralentissement
							    residuel et saturation memoire au chargement de certains niveaux.
							    Mieux vaut le dire que laisser croire a une page cassee. */}
							{IS_FIREFOX && (
								<div className="notice">
									<span>
										{t('warn.firefox')}{' '}
										<button className="notice-link" onClick={() => setView('about')}>
											{t('nav.about')}
										</button>
										.
									</span>
								</div>
							)}

							{bootError && (
								<div className="notice notice-error">
									<span>
										{t('err.disc.title')} — {t('err.disc.formats')}
									</span>
									<button className="notice-link" onClick={() => setBootError(null)}>
										{t('err.close')}
									</button>
								</div>
							)}

							<div className="rows">
								<div className="row-head">
									<span className="col-title">{t('lib.col.title')}</span>
									<span className="col-code">{t('lib.col.code')}</span>
									<span className="col-region">{t('lib.col.region')}</span>
									<span className="col-size">{t('lib.col.size')}</span>
									<span className="col-compat">{t('lib.col.compat')}</span>
									<span className="col-last">{t('lib.col.last')}</span>
								</div>

								{entries.map((entry, index) => {
									const compat: CompatEntry | null = compatReady ? lookupCompat(entry.code) : null;
									// Le repere 3 se pose sur la premiere ligne seulement : il
									// designe le geste, pas ce jeu-la en particulier.
									const marked = tutorial && index === 0;
									return (
										<div className="row-wrap" key={entry.id}>
											<button
												className={booted === entry.id ? 'row row-paused' : 'row'}
												onClick={() => open(entry)}
												onContextMenu={(e) => remove(entry, e)}
												onMouseEnter={marked ? () => setTip('entry') : undefined}
												onMouseLeave={marked ? () => setTip(null) : undefined}
												title={entry.origin === 'local' ? t('lib.remove.hint') : undefined}
											>
												<span className="col-title">{entry.title}</span>
												<span className="col-code">{entry.code || '—'}</span>
												<span className="col-region">{entry.region || '—'}</span>
												<span className="col-size">{formatSizeI18n(entry.size, lang, t)}</span>
												<span className="col-compat">
													{compat ? (
														<span className={`st-${compat.etat}`}>
															{t(COMPAT_KEY[compat.etat] as Key)}
														</span>
													) : (
														<span className="st-none">
															{compatReady ? t('compat.unlisted') : '—'}
														</span>
													)}
												</span>
												<span
													className={
														booted === entry.id ? 'col-last col-last-on' : 'col-last'
													}
												>
													{booted === entry.id
														? t('lib.paused')
														: formatLastPlayedI18n(entry.lastPlayed, lang, t)}
												</span>
											</button>
											{marked && (
												<>
													<span className="tut-badge" style={{ top: 8, left: -9 }}>
														3
													</span>
													<Tip
														show={showTip('entry')}
														text={t('tut.entry')}
														style={{ top: 'calc(100% + 6px)', left: 0 }}
													/>
												</>
											)}
										</div>
									);
								})}
							</div>

							{/* Rappel du geste de depot, sous la table : il reste vrai
							    meme quand la bibliotheque n'est plus vide. */}
							<div className="library-foot">
								<span
									className="tut-anchor empty-foot"
									onMouseEnter={() => setTip('ctrl')}
									onMouseLeave={() => setTip(null)}
								>
									{t('lib.drop')} {t('lib.drop.note')}
									{tutorial && (
										<>
											<span className="tut-badge" style={{ top: -6, left: -26 }}>
												4
											</span>
											<Tip
												show={showTip('ctrl')}
												text={t('tut.ctrl')}
												style={{ bottom: 'calc(100% + 12px)', left: 0 }}
											/>
										</>
									)}
								</span>

								{/* La demo reste accessible une fois la bibliotheque
								    garnie, mais sur une seule ligne : le visiteur a
								    deja ses jeux, elle ne doit plus leur disputer
								    l'attention. */}
								<span className="demo-inline">
									{t('demo.compact')}{' '}
									<button className="demo-inline-link" onClick={() => open(DEMO)}>
										{booted === DEMO.id ? t('demo.resume') : t('demo.compact.link')}
									</button>
									{booted === DEMO.id && (
										<span className="demo-inline-state"> · {t('demo.paused')}</span>
									)}
								</span>
							</div>
						</>
					)}

					<input
						ref={fileInputRef}
						type="file"
						accept=".iso,.cso,.chd,.isz,.bin,.elf"
						style={{ display: 'none' }}
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) addFile(file);
							e.target.value = '';
						}}
					/>
				</div>
			)}

			{/* Barre du tutoriel : elle nomme ce qui se passe et offre d'en sortir. */}
			{tutorial && view === 'library' && (
				<div className="notice" style={{ margin: '0 40px 24px', maxWidth: 'none' }}>
					{/* Le compte suit ce qui est reellement affiche : sur une
					    bibliotheque vide, les reperes 3 et 4 n'existent pas. */}
					<span className="tut-bar">
						<span className="pad-dot" />
						{t('tut.active')} · {entries.length === 0 ? 2 : 4} {t('tut.markers')}
					</span>
					<span className="topbar-spacer" />
					<button className="mini" onClick={dismissTutorial}>
						{t('tut.dismiss')}
					</button>
				</div>
			)}

			{/* Une fois le tutoriel ecarte, on doit pouvoir le rappeler. */}
			{!tutorial && view === 'library' && entries.length > 0 && (
				<div style={{ padding: '0 40px 24px' }}>
					<button className="mini" onClick={() => setTutorial(true)}>
						{t('tut.replay')}
					</button>
				</div>
			)}

			<StatusBar platform={platform} pad={pad} />
		</div>
	);
}

export default App;
